import type { ReactNode } from "react";
import { Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";

const DEFAULT_GRID_CLASSNAME =
  "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6";

export interface EntityListPageProps<T> {
  /** Page title, e.g. "Clients". */
  title: string;
  /** Subtitle rendered under the title. */
  description?: string;
  /** The items to render once loaded. */
  items: T[];
  /** Shows the loading state instead of items/empty state. */
  loading?: boolean;
  /** Shows an error state instead of items/empty state. Ignored while `loading`. */
  error?: string | null;
  /** Stable key for each item, e.g. `(client) => client.id`. */
  getItemKey: (item: T) => string;
  /** Renders a single item - typically a `<Card>` wrapped in a `<Link>`. */
  renderItem: (item: T) => ReactNode;
  /** Route to the create page. Renders the create button as a link to it. */
  createHref?: string;
  /** Overrides the create button's label. Defaults to "Create". */
  createLabel?: string;
  /** Alternative to `createHref` for non-navigation create flows. */
  onCreateClick?: () => void;
  /** Fully overrides the empty state. Defaults to a centered message + create CTA. */
  emptyState?: ReactNode;
  /** Message shown in the default empty state. */
  emptyMessage?: string;
  /** Extra controls rendered next to the create button, e.g. filters. */
  headerActions?: ReactNode;
  /** Overrides the item grid's className. Defaults to a responsive 1/2/3-col grid. */
  gridClassName?: string;
}

/**
 * Shared shell for "list existing X" pages - header (title + create button),
 * loading/error/empty states, and an item grid. Extracted from
 * routes/dashboard/clients/index.tsx; see the README in this directory for the
 * conventions this and its sibling components (`EntityFormPage`, `FormSection`)
 * are meant to establish.
 */
export function EntityListPage<T>({
  title,
  description,
  items,
  loading = false,
  error = null,
  getItemKey,
  renderItem,
  createHref,
  createLabel = "Create",
  onCreateClick,
  emptyState,
  emptyMessage = "No items found. Create your first one to get started.",
  headerActions,
  gridClassName = DEFAULT_GRID_CLASSNAME,
}: EntityListPageProps<T>) {
  // items is typed as required/non-nullable, but this component has many
  // future call sites (Agent/Integration/User lists, etc.) that will source
  // it from async data - normalize defensively so a caller mid-load (e.g. an
  // uninitialized query hook) can't crash this shared shell.
  const safeItems = items ?? [];
  const createButton =
    createHref || onCreateClick ? (
      <CreateButton href={createHref} onClick={onCreateClick} label={createLabel} />
    ) : null;

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">{title}</h1>
            {description && (
              <p className="text-lg text-muted-foreground">{description}</p>
            )}
          </div>
          {(headerActions || createButton) && (
            <div className="flex items-center gap-2">
              {headerActions}
              {createButton}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : safeItems.length === 0 ? (
          emptyState ?? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">{emptyMessage}</p>
                  {createButton}
                </div>
              </CardContent>
            </Card>
          )
        ) : (
          <div className={gridClassName}>
            {safeItems.map((item) => (
              <div key={getItemKey(item)}>{renderItem(item)}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateButton({
  href,
  onClick,
  label,
}: {
  href?: string;
  onClick?: () => void;
  label: string;
}) {
  if (href) {
    return (
      <LinkAsButton to={href}>
        <Plus className="w-4 h-4 mr-2" />
        {label}
      </LinkAsButton>
    );
  }
  return (
    <Button onClick={onClick}>
      <Plus className="w-4 h-4 mr-2" />
      {label}
    </Button>
  );
}
